import React, { useState } from 'react';
import { Box, TextField, Button, Typography, Alert, IconButton } from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';


const DemandInputForm = ({ onClose, onFormChange, onSubmit, isSQSMode }) => {
    const [rowCount, setRowCount] = useState(3);
    const MAX_ROWS = 20;

    const [formData, setFormData] = useState({
        ipn: Array(MAX_ROWS).fill(''),
        mpn: Array(MAX_ROWS).fill(''),
        manufacturer: Array(MAX_ROWS).fill(''),
        quantity: Array(MAX_ROWS).fill(''),
        targetPrice: Array(MAX_ROWS).fill(''),
        dueDate: Array(MAX_ROWS).fill(''),
        reqNumber: Array(MAX_ROWS).fill('')
    });

    const addRow = () => {
        if (rowCount < MAX_ROWS) {
            setRowCount(rowCount + 1);
        }
    };

    const clearRow = (rowIndex) => {
        const newFormData = { ...formData };
        Object.keys(newFormData).forEach(field => {
            newFormData[field][rowIndex] = '';
        });
        setFormData(newFormData);
        const hasValues = Object.values(newFormData).some(arr => 
            arr.some(value => value.trim() !== '')
        );
        onFormChange(hasValues, newFormData);
    };

    const validateAndFormatInput = (value, fieldId) => {
        if (!value.trim()) return '';

        switch (fieldId) {
            case 'quantity':
                // Remove any non-numeric characters except decimal point
                const cleanQty = value.replace(/[^0-9.]/g, '');
                return isNaN(parseFloat(cleanQty)) ? '' : cleanQty;

            case 'targetPrice':
                // Allow $ in the input but remove it for validation
                const valueWithoutSymbol = value.replace('$', '').replace(/,/g, '');
                const cleanPrice = valueWithoutSymbol.replace(/[^0-9.]/g, '');
                if (isNaN(parseFloat(cleanPrice))) return '';
                // Keep the $ in the display value but ensure it's at the start
                return value.startsWith('$') ? value : `$${value}`;

            case 'dueDate':
                // Validate date format (MM/DD/YYYY)
                const dateRegex = /^(0[1-9]|1[0-2])\/(0[1-9]|[12][0-9]|3[01])\/\d{4}$/;
                return dateRegex.test(value) ? value : '';

            default:
                // For text fields (ipn, mpn, manufacturer, reqNumber)
                // Remove any special characters that might cause issues
                return value.replace(/[^\w\s-]/g, '').trim();
        }
    };

    const handleInputChange = (field, index, value) => {
        const formattedValue = validateAndFormatInput(value, field);
        
        const newFormData = {
            ...formData,
            [field]: formData[field].map((item, i) => i === index ? formattedValue : item)
        };
        setFormData(newFormData);
        
        const hasValues = Object.values(newFormData).some(arr => 
            arr.some(value => value.trim() !== '')
        );
        console.log("DemandInputForm hasValues:", hasValues);
        onFormChange(hasValues, newFormData);
    };

    const handlePaste = (event, rowIndex, fieldId) => {
        event.preventDefault();
        
        // Get the clipboard data
        const clipboardData = event.clipboardData.getData('text');
        
        // Split by newlines to get rows, then split each row by tabs
        // Filter out empty rows that might come from trailing newlines
        const rows = clipboardData.split('\n')
            .filter(row => row.trim() !== '')
            .map(row => row.split('\t'));
        
        // Create a new form data object
        const newFormData = { ...formData };
        
        // Find the starting column index based on the clicked field
        const startColumnIndex = fields.findIndex(field => field.id === fieldId);
        
        // Process each row of pasted data
        rows.forEach((rowData, rowOffset) => {
            // Calculate the target row index in the form
            const targetRowIndex = rowIndex + rowOffset;
            
            // Skip if we've exceeded the form's row count
            if (targetRowIndex >= rowCount) {
                return;
            }
            
            // Process each cell in the row
            rowData.forEach((cellValue, colOffset) => {
                const targetColumnIndex = startColumnIndex + colOffset;
                
                // Skip if we've exceeded the number of fields
                if (targetColumnIndex >= fields.length) {
                    return;
                }
                
                // Get the field ID for this column
                const targetFieldId = fields[targetColumnIndex].id;
                
                // Validate and format the input based on the field type
                const formattedValue = validateAndFormatInput(cellValue.trim(), targetFieldId);
                
                // Update the form data
                newFormData[targetFieldId][targetRowIndex] = formattedValue;
            });
        });
        
        // Update the form state
        setFormData(newFormData);
        
        // Check if any field has a value and update form state
        const hasValues = Object.values(newFormData).some(arr => 
            arr.some(value => value.trim() !== '')
        );
        
        // Notify parent component of changes
        onFormChange(hasValues, newFormData);
        
        // Automatically add rows if needed
        const neededRows = rowIndex + rows.length;
        if (neededRows > rowCount && neededRows <= MAX_ROWS) {
            setRowCount(neededRows);
        }
    };

    const fields = [
        { id: 'ipn', label: 'IPN' },
        { id: 'mpn', label: 'MPN' },
        { id: 'manufacturer', label: 'MANUFACTURER' },
        { id: 'quantity', label: 'QUANTITY' },
        { id: 'targetPrice', label: 'TARGET PRICE' },
        { id: 'dueDate', label: 'DUE DATE' },
        { id: 'reqNumber', label: 'REQ NUMBER' }
    ];

    const hasValidInput = () => {
        const hasRequiredFields = formData.ipn.some(val => val.trim() !== '') ||
                                formData.mpn.some(val => val.trim() !== '') ||
                                formData.manufacturer.some(val => val.trim() !== '');
        
        // Only enable submit if both required fields are filled AND a supply source is selected
        return hasRequiredFields && isSQSMode;
    };

    const getHelperText = (fieldId, value) => {
        // Only show helper text if there's an error and a value
        if (!value || !hasError(fieldId, value)) return '';
        
        switch (fieldId) {
            case 'quantity':
                return 'Must be a numeric value';
            case 'targetPrice':
                return 'Must be a numeric value';
            case 'dueDate':
                return 'Must be in MM/DD/YYYY format';
            default:
                return '';
        }
    };

    const hasError = (fieldId, value) => {
        if (!value) return false;
        
        switch (fieldId) {
            case 'quantity':
                return isNaN(parseFloat(value));
            case 'targetPrice':
                const cleanValue = value.replace('$', '').replace(/,/g, '');
                return isNaN(parseFloat(cleanValue));
            case 'dueDate':
                const dateRegex = /^(0[1-9]|1[0-2])\/(0[1-9]|[12][0-9]|3[01])\/\d{4}$/;
                return !dateRegex.test(value);
            default:
                return false;
        }
    };

    // Add a preprocessing step before submitting
    const handleSubmit = (formData) => {
        // Create a copy of the form data
        const processedData = {
            ...formData,
            targetPrice: formData.targetPrice.map(price => 
                price ? price.replace('$', '').replace(/,/g, '') : ''
            )
        };
        
        onSubmit(processedData);
    };

    return (
        <Box sx={{ 
            width: '100%',
            backgroundColor: '#fff',
            borderRadius: '4px',
            border: '1px solid #ddd',
            padding: '20px',
        }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
                Enter Demand Information
            </Typography>
            
            <Alert severity="info" sx={{ mb: 3 }}>
                Enter one or more MPNs, IPNs or Manufacturers (and other optional information), 
                select Supply Sources and click the submit button to run a supply matching search.
            </Alert>

            {/* Column Headers */}
            <Box sx={{ 
                display: 'grid',
                gridTemplateColumns: 'repeat(7, 1fr) auto',
                gap: 1,
                mb: 2,
                '& > div': {
                    textAlign: 'center',
                    fontWeight: '500',
                    color: '#333',
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    whiteSpace: 'nowrap'
                }
            }}>
                {fields.map(field => (
                    <Box key={field.id}>{field.label}</Box>
                ))}
            </Box>

            {/* Scrollable container for input rows */}
            <Box sx={{ 
                maxHeight: '400px', // Adjust this value as needed
                overflowY: 'auto',
                mb: 2
            }}>
                {/* Input Rows */}
                {[...Array(rowCount)].map((_, rowIndex) => (
                    <Box 
                        key={rowIndex}
                        sx={{ 
                            display: 'grid',
                            gridTemplateColumns: 'repeat(7, 1fr) auto',
                            gap: 1,
                            mb: 1
                        }}
                    >
                        {fields.map(field => (
                            <TextField
                                key={field.id}
                                size="small"
                                value={formData[field.id][rowIndex]}
                                onChange={(e) => handleInputChange(field.id, rowIndex, e.target.value)}
                                onPaste={(e) => handlePaste(e, rowIndex, field.id)}
                                helperText={getHelperText(field.id, formData[field.id][rowIndex])}
                                error={hasError(field.id, formData[field.id][rowIndex])}
                                sx={{ 
                                    width: '100%',
                                    '& .MuiOutlinedInput-root': {
                                        backgroundColor: '#fff'
                                    }
                                }}
                            />
                        ))}
                        <IconButton
                            onClick={() => clearRow(rowIndex)}
                            sx={{
                                color: '#dc3545',
                                padding: '4px',
                                '&:hover': {
                                    backgroundColor: 'rgba(220, 53, 69, 0.04)'
                                }
                            }}
                        >
                            <RemoveCircleIcon />
                        </IconButton>
                    </Box>
                ))}
            </Box>

            {/* Add Row Button */}
            {rowCount < MAX_ROWS && (
                <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'flex-end',  // Changed from 'center' to 'flex-end'
                    mb: 2,
                    pr: 2  // Add some padding from the right edge
                }}>
                    <IconButton 
                        onClick={addRow}
                        sx={{ 
                            color: '#1976d2',
                            '&:hover': {
                                backgroundColor: 'rgba(25, 118, 210, 0.04)'
                            }
                        }}
                    >
                        <AddCircleOutlineIcon />
                    </IconButton>
                </Box>
            )}

            {/* Submit Button */}
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                <Button 
                    variant="contained" 
                    onClick={() => handleSubmit(formData)}
                    disabled={!hasValidInput()}
                    sx={{ 
                        bgcolor: '#1976d2',
                        '&:hover': {
                            bgcolor: '#1565c0'
                        },
                        '&.Mui-disabled': {
                            bgcolor: '#ccc'
                        }
                    }}
                >
                    Submit for Supply Matching
                </Button>
            </Box>
        </Box>
    );
};

export default DemandInputForm; 